<template>
  <v-container v-if="$can('browse', 'ServiceTasksRepository')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Service Tasks Repository
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          color="primary"
          class="ma-0"
          @click="openServiceTaskRepositoryEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.title"
          solo
          hide-details
          clearable
          label="Title"
        />
        <v-switch
          v-model="filtersDirty.wintering"
          solo
          hide-details
          label="Only Wintering"
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="serviceTasksRepository.meta.from"
          :to="serviceTasksRepository.meta.to"
          :total="serviceTasksRepository.meta.total"
          :last-page="serviceTasksRepository.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="serviceTasksRepository.data"
          :loading="serviceTasksRepository.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="serviceTasksRepository.meta.total"
          hide-default-footer
          must-sort
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <service-task-repository-row
              :service-task="item"
              @click.native="openServiceTaskRepositoryEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no service tasks in repository at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  class="ma-0"
                  small
                  @click="openServiceTaskRepositoryEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
        </v-data-table>
        <pagination
          :from="serviceTasksRepository.meta.from"
          :to="serviceTasksRepository.meta.to"
          :total="serviceTasksRepository.meta.total"
          :last-page="serviceTasksRepository.meta.last_page"
          :page.sync="pagination.page"
          class="mt-1"
        />
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <service-task-repository-edit-dialog
      :visible.sync="serviceTaskRepositoryEditDialog"
      :service-task="serviceTaskRepositoryDirty"
      @serviceTaskRepository:created="serviceTaskRepositoryCreated"
      @serviceTaskRepository:updated="serviceTaskRepositoryUpdated"
      @serviceTaskRepository:deleted="serviceTaskRepositoryDeleted"
    />
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import filterableMixin from '@/mixins/filterableMixin'
import paginationMixin from '@/mixins/paginationMixin'
import ServiceTaskRepository from '@/models/ServiceTaskRepository'
import ServiceTaskRepositoryRow from '@/components/settings/serviceTasksRepository/ServiceTaskRepositoryRow'
import ServiceTaskRepositoryEditDialog
  from '@/components/settings/serviceTasksRepository/ServiceTaskRepositoryEditDialog'

export default {
    name: 'ServiceTasksRepository',
    components: {
      ServiceTaskRepositoryRow,
      ServiceTaskRepositoryEditDialog
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        serviceTasksRepository: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Title', value: 'title', sortable: true },
          { text: 'Type', value: 'type.name', sortable: false },
          { text: 'Order', value: 'order', sortable: true },
          { text: 'Wintering', value: 'wintering', sortable: false }
        ],
        serviceTaskRepositoryDirty: undefined,
        serviceTaskRepositoryEditDialog: false,
        filtersClean: ServiceTaskRepository.emptyFilters,
        filtersDirty: cloneDeep(ServiceTaskRepository.emptyFilters)
      }
    },
    created() {
      this.fetchServiceTasksRepository()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchServiceTasksRepository()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchServiceTasksRepository() {
        this.serviceTasksRepository.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          wintering: this.filterQueryParams.wintering ? true : undefined
        }

        try {
          let result = await ServiceTaskRepository.api.fetch(queryParams)

          this.serviceTasksRepository.data = result.data
          this.serviceTasksRepository.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.serviceTasksRepository.loading = false
        }
      },
      openServiceTaskRepositoryEditDialog(serviceTaskRepository = undefined) {
        this.serviceTaskRepositoryDirty = cloneDeep(serviceTaskRepository)
        this.serviceTaskRepositoryEditDialog = true
      },
      serviceTaskRepositoryCreated(serviceTaskRepository) {
        this.serviceTasksRepository.data.unshift(serviceTaskRepository)
      },
      serviceTaskRepositoryUpdated(serviceTaskRepository) {
        const index = this.serviceTasksRepository.data.findIndex(s => s.id === serviceTaskRepository.id)

        this.serviceTasksRepository.data.splice(index, 1, serviceTaskRepository)
      },
      serviceTaskRepositoryDeleted(serviceTaskRepositoryId) {
        this.fetchServiceTasksRepository()
      }
    }
  }
</script>
